import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Field, GrupoCampos, Row } from "../types";
import { api, ApiError, type OsEncontrada } from "../api";
import { ICONS, IconClose, IconCheck, IconPlus, IconAlert, IconSearch, type IconName } from "../icons";
import StatusCardSelector from "./StatusCardSelector";
import ChecklistCardSelector from "./ChecklistCardSelector";

/**
 * O POP-UP DE CADASTRO do laboratório — o mesmo para todas as abas.
 *
 * Ele nasce da definição de campos (src/config.ts), então tudo o que melhora
 * aqui melhora em Análises, Material do cliente, Quebra de frasco, Equipamentos,
 * Limites normativos e Razões NBR 7274 de uma vez.
 *
 * O que a tela faz além de mostrar caixinhas:
 *  - AGRUPA os campos em seções numeradas ("O que aconteceu", "Onde e por quê"),
 *    porque um paredão de 8 campos soltos não diz por onde começar;
 *  - escolha curta (até 4 opções) vira BOTÃO, não `<select>`: "Frasco/Seringa"
 *    em dois cliques, com a mesma cara dos cartões de status do sistema;
 *  - número ganha − e +, data ganha "Hoje", texto ganha sugestões do próprio
 *    laboratório, e cada campo pode trazer a linha de ajuda que só quem está na
 *    bancada sabe;
 *  - a CONFERÊNCIA é nossa, não do navegador: o que falta aparece em vermelho
 *    no campo e contado no rodapé, em vez do balão nativo que some sozinho;
 *  - Esc fecha, Ctrl+Enter salva.
 *
 * Os componentes auxiliares ficam FORA do componente de propósito: declarados
 * dentro, o React os trataria como um tipo novo a cada tecla e remontaria o
 * campo — o cursor pularia para fora no meio da digitação.
 */

interface Props {
  title: string;
  fields: Field[];
  initial?: Row | null;
  /** Cabeçalho: ícone da aba, o que é o registro e para que serve preencher. */
  icon?: IconName;
  subtitle?: string;
  grupos?: GrupoCampos[];
  onCancel: () => void;
  onSubmit: (data: Row) => Promise<void>;
}

const hoje = () => new Date().toISOString().slice(0, 10);

/** O valor conta como preenchido? (0 conta; "" e lista vazia, não.) */
function preenchido(v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return String(v ?? "").trim() !== "";
}

export default function RecordForm({
  title,
  fields,
  initial,
  icon,
  subtitle,
  grupos,
  onCancel,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<Row>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Campos obrigatórios que ficaram vazios (só depois de tentar salvar). */
  const [faltando, setFaltando] = useState<string[]>([]);
  /** O que a OS escolhida na lupa trouxe (fica à vista até fechar). */
  const [avisoOs, setAvisoOs] = useState<{
    numero: string;
    fontes: string[];
    preenchidos: string[];
    jaTemRemessa: boolean;
  } | null>(null);
  const corpoRef = useRef<HTMLFormElement>(null);

  // Campos calculados no banco (ex.: saldo, divergência, lead time) não são editáveis.
  const editable = useMemo(() => fields.filter((f) => !f.computed), [fields]);

  useEffect(() => {
    const base: Row = {};
    for (const f of editable) {
      let v = initial ? initial[f.name] : undefined;
      if (f.type === "date" && v) v = String(v).slice(0, 10); // YYYY-MM-DD
      if (f.type === "checklist") v = Array.isArray(v) ? v : [];
      // Registro NOVO: a data do fato é quase sempre hoje — digitar de novo
      // toda vez é trabalho à toa (e é onde nasce data errada).
      if (!initial && f.type === "date" && f.padraoHoje && !v) v = hoje();
      // Registro NOVO com valor padrão (ver Field.padrao): o caso comum já
      // vem escolhido, e quem precisa de outro troca.
      if (!initial && f.padrao !== undefined && (v === undefined || v === "")) v = f.padrao;
      base[f.name] = v ?? "";
    }
    setForm(base);
    setError(null);
    setFaltando([]);
    setAvisoOs(null);
  }, [initial, editable]);

  // Esc fecha; Ctrl+Enter salva de qualquer campo (inclusive da observação).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onCancel();
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        corpoRef.current?.requestSubmit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, saving]);

  // Foco no primeiro campo — só no computador. No celular, focar sozinho abre
  // o teclado por cima do formulário antes de a pessoa ver o que é para fazer.
  useEffect(() => {
    if (!window.matchMedia?.("(pointer: fine)").matches) return;
    const alvo = corpoRef.current?.querySelector<HTMLElement>(
      "input:not([type=hidden]), select, textarea",
    );
    alvo?.focus({ preventScroll: true });
  }, [editable]);

  const set = (name: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFaltando((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : prev));
  };

  /**
   * A OS escolhida na lupa preenche o resto do formulário.
   *
   * Só entra onde está VAZIO: quem já digitou alguma coisa não pode ver o que
   * escreveu ser trocado por baixo. O que foi preenchido aparece no aviso, para
   * a pessoa conferir antes de salvar.
   */
  function aplicarOs(f: Field, os: OsEncontrada) {
    const mapa = f.preencheDaOs ?? {};
    const preenchidos: string[] = [];
    setForm((prev) => {
      const novo = { ...prev, [f.name]: os.numero };
      for (const [campo, chaveOs] of Object.entries(mapa)) {
        if (!editable.some((c) => c.name === campo)) continue;
        const atual = String(novo[campo] ?? "").trim();
        if (atual !== "") continue;
        const valor =
          chaveOs.startsWith("dest_")
            ? os.destinatario?.[chaveOs]
            : (os as unknown as Record<string, unknown>)[chaveOs];
        const texto = String(valor ?? "").trim();
        if (texto === "") continue;
        novo[campo] = texto;
        preenchidos.push(editable.find((c) => c.name === campo)?.label ?? campo);
      }
      return novo;
    });
    setFaltando([]);
    setAvisoOs({
      numero: os.numero,
      fontes: os.fontes,
      preenchidos,
      jaTemRemessa: !!os.ja_tem_remessa,
    });
  }

  /** Os campos de cada seção, na ordem da config. */
  const secoes = useMemo(() => {
    if (!grupos?.length) return [{ grupo: null as GrupoCampos | null, campos: editable }];
    const usados = new Set<string>();
    const blocos = grupos.map((g) => {
      const campos = editable.filter((f) => f.group === g.key);
      campos.forEach((f) => usados.add(f.name));
      return { grupo: g, campos };
    });
    const sobra = editable.filter((f) => !usados.has(f.name));
    if (sobra.length) {
      blocos.push({ grupo: { key: "__outros", label: "Outros dados" }, campos: sobra });
    }
    return blocos.filter((b) => b.campos.length > 0);
  }, [grupos, editable]);

  const obrigatorios = editable.filter((f) => f.required);
  const pendentes = obrigatorios.filter((f) => !preenchido(form[f.name]));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Conferência própria (o <form> vai com noValidate): o balão do navegador
    // aparecia atrás do modal em campo rolado para fora da vista, e a pessoa
    // ficava clicando em Salvar sem entender o que faltava.
    if (pendentes.length > 0) {
      const nomes = pendentes.map((f) => f.name);
      setFaltando(nomes);
      const alvo = corpoRef.current?.querySelector<HTMLElement>(`[data-campo="${nomes[0]}"]`);
      alvo?.scrollIntoView({ behavior: "smooth", block: "center" });
      alvo?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
      return;
    }

    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  const Icone = icon ? ICONS[icon] : null;

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card rf-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            {Icone && (
              <span className="rf-head-icon">
                <Icone size={20} />
              </span>
            )}
            <div>
              <h3>{title}</h3>
              {subtitle && <p className="rf-head-sub">{subtitle}</p>}
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancel} title="Fechar (Esc)">
            <IconClose size={18} />
          </button>
        </div>

        {/* O rodapé fica FORA do form (preso embaixo, sem entrar na rolagem):
            o botão Salvar continua enviando este form pelo atributo `form`. */}
        <form id="record-form" ref={corpoRef} className="modal-body rf-body" onSubmit={submit} noValidate>
          {avisoOs && (
            <div className={`rf-os-aviso${avisoOs.jaTemRemessa ? " atencao" : ""}`}>
              <IconCheck size={15} />
              <div>
                <strong>OS {avisoOs.numero}</strong> encontrada em{" "}
                {avisoOs.fontes.join(" + ") || "nenhum sistema"}.{" "}
                {avisoOs.preenchidos.length > 0
                  ? `Preenchi ${avisoOs.preenchidos.join(", ")} — confira antes de salvar.`
                  : "Os campos já tinham conteúdo, então nada foi trocado."}
                {avisoOs.jaTemRemessa && (
                  <div className="rf-os-repetida">
                    Atenção: já existe remessa de material cadastrada para esta OS.
                  </div>
                )}
              </div>
              <button type="button" className="icon-btn" onClick={() => setAvisoOs(null)} title="Fechar aviso">
                <IconClose size={15} />
              </button>
            </div>
          )}
          {secoes.map((s, i) => (
            <Secao
              key={s.grupo?.key ?? "unico"}
              numero={secoes.length > 1 ? i + 1 : null}
              grupo={s.grupo}
              campos={s.campos}
              form={form}
              faltando={faltando}
              set={set}
              aplicarOs={aplicarOs}
            />
          ))}
        </form>

        <div className="modal-foot rf-foot">
          {error && <div className="form-error">{error}</div>}
          {!error && (
            <span className="rf-status">
              {obrigatorios.length === 0 ? (
                "Nenhum campo obrigatório"
              ) : pendentes.length === 0 ? (
                <>
                  <IconCheck size={14} /> Tudo pronto para salvar
                </>
              ) : (
                <>
                  <em className="req">*</em> {pendentes.length} campo(s) obrigatório(s) por preencher
                </>
              )}
            </span>
          )}
          <span className="rf-atalho" title="Atalhos deste formulário">Esc fecha · Ctrl+Enter salva</span>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" form="record-form" className="btn-primary" disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 *  Seção do formulário                                              *
 * ---------------------------------------------------------------- */

function Secao({
  numero,
  grupo,
  campos,
  form,
  faltando,
  set,
  aplicarOs,
}: {
  numero: number | null;
  grupo: GrupoCampos | null;
  campos: Field[];
  form: Row;
  faltando: string[];
  set: (name: string, value: unknown) => void;
  aplicarOs: (f: Field, os: OsEncontrada) => void;
}) {
  const Icone = grupo?.icon ? ICONS[grupo.icon] : null;
  return (
    <section className="rf-secao">
      {grupo && (
        <header className="rf-secao-head">
          {numero != null && <span className="rf-secao-num">{numero}</span>}
          {Icone && <Icone size={15} />}
          <h4>{grupo.label}</h4>
          {grupo.hint && <span className="rf-secao-hint">{grupo.hint}</span>}
        </header>
      )}
      <div className="form-grid">
        {campos.map((f) => (
          <Campo
            key={f.name}
            f={f}
            valor={form[f.name]}
            erro={faltando.includes(f.name)}
            set={set}
            aplicarOs={aplicarOs}
          />
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- *
 *  Um campo (rótulo + entrada + ajuda)                              *
 * ---------------------------------------------------------------- */

function Campo({
  f,
  valor,
  erro,
  set,
  aplicarOs,
}: {
  f: Field;
  valor: unknown;
  erro: boolean;
  set: (name: string, value: unknown) => void;
  aplicarOs: (f: Field, os: OsEncontrada) => void;
}) {
  const largo =
    f.full || f.type === "textarea" || f.type === "status" || f.type === "checklist" || ehChips(f);
  // `label` embrulha o campo e dá o clique no rótulo — mas com botões dentro
  // (chips, cartões, a lista da lupa) o clique no rótulo acionaria o botão.
  const Wrapper: "div" | "label" =
    f.type === "status" || f.type === "checklist" || ehChips(f) || f.buscaOs ? "div" : "label";

  return (
    <Wrapper
      className={`field rf-field${largo ? " field-wide" : ""}${erro ? " rf-erro" : ""}`}
      data-campo={f.name}
    >
      <span className="rf-rotulo">
        {f.label}
        {f.required && <em className="req"> *</em>}
      </span>
      {f.buscaOs ? (
        <BuscaOs f={f} valor={String(valor ?? "")} erro={erro} set={set} aplicarOs={aplicarOs} />
      ) : (
        renderInput(f, valor, set, erro)
      )}
      {erro ? (
        <small className="rf-ajuda rf-ajuda-erro">
          <IconAlert size={12} /> Preencha este campo
        </small>
      ) : (
        f.hint && <small className="rf-ajuda">{f.hint}</small>
      )}
    </Wrapper>
  );
}

/* ---------------------------------------------------------------- *
 *  A LUPA DA OS                                                     *
 * ---------------------------------------------------------------- */

/**
 * Campo de OS que procura a ordem de serviço em TODOS OS SISTEMAS enquanto a
 * pessoa digita (Relatórios + Laboratório + Gestão da Operação) e, ao escolher
 * uma, preenche sozinho o resto do formulário.
 *
 * Detalhes que fazem diferença na bancada:
 *  - só busca a partir de 2 caracteres e espera 350 ms parado (senão seria uma
 *    consulta a três sistemas por tecla digitada);
 *  - a busca anterior é CANCELADA quando chega outra (AbortController), então a
 *    lista nunca mostra o resultado de um texto que já mudou;
 *  - dá para ignorar tudo e digitar o número na mão — a lista é ajuda, não trava;
 *  - navega com ↑ ↓, escolhe com Enter, fecha com Esc (Esc aqui NÃO fecha o
 *    formulário: fecha só a lista);
 *  - se um sistema estiver fora do ar, o aviso aparece embaixo e a busca segue
 *    com os que responderam.
 */
function BuscaOs({
  f,
  valor,
  erro,
  set,
  aplicarOs,
}: {
  f: Field;
  valor: string;
  erro: boolean;
  set: (name: string, value: unknown) => void;
  aplicarOs: (f: Field, os: OsEncontrada) => void;
}) {
  const [itens, setItens] = useState<OsEncontrada[]>([]);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [aberta, setAberta] = useState(false);
  const [erroBusca, setErroBusca] = useState<string | null>(null);
  const [marcado, setMarcado] = useState(0);
  /** Texto escolhido na lista: não busca de novo só porque o campo mudou. */
  const escolhidoRef = useRef<string | null>(null);
  const caixaRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const procurar = useCallback(async (texto: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setBuscando(true);
    setErroBusca(null);
    try {
      const r = await api.os.buscar(texto, controller.signal);
      setItens(r.itens ?? []);
      setAvisos(r.avisos ?? []);
      setMarcado(0);
      setAberta(true);
    } catch (e) {
      if ((e as Error)?.name === "AbortError") return;
      setItens([]);
      setErroBusca(
        e instanceof ApiError ? e.message : "Não consegui consultar as OS agora — digite à mão.",
      );
      setAberta(true);
    } finally {
      if (!controller.signal.aborted) setBuscando(false);
    }
  }, []);

  // Espera a pessoa parar de digitar antes de perguntar aos três sistemas.
  useEffect(() => {
    const texto = valor.trim();
    if (escolhidoRef.current === texto) return;
    if (texto.length < 2) {
      setItens([]);
      setAberta(false);
      return;
    }
    const t = setTimeout(() => void procurar(texto), 350);
    return () => clearTimeout(t);
  }, [valor, procurar]);

  // Clique fora fecha a lista (sem mexer no que já foi digitado).
  useEffect(() => {
    if (!aberta) return;
    const fora = (e: MouseEvent) => {
      if (!caixaRef.current?.contains(e.target as Node)) setAberta(false);
    };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, [aberta]);

  function escolher(os: OsEncontrada) {
    escolhidoRef.current = os.numero.trim();
    aplicarOs(f, os);
    setAberta(false);
  }

  function teclado(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!aberta || itens.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMarcado((m) => (m + 1) % itens.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMarcado((m) => (m - 1 + itens.length) % itens.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const alvo = itens[marcado];
      if (alvo) escolher(alvo);
    } else if (e.key === "Escape") {
      // Só a lista fecha — o formulário continua aberto (o Esc dele é global).
      e.stopPropagation();
      setAberta(false);
    }
  }

  return (
    <div className="rf-os" ref={caixaRef}>
      <div className="rf-entrada rf-os-campo">
        <IconSearch size={15} className="rf-os-lupa" />
        <input
          type="text"
          value={valor}
          aria-invalid={erro || undefined}
          placeholder={f.placeholder ?? "digite o número da OS ou o cliente"}
          autoComplete="off"
          onChange={(e) => {
            escolhidoRef.current = null;
            set(f.name, e.target.value);
          }}
          onFocus={() => itens.length > 0 && setAberta(true)}
          onKeyDown={teclado}
        />
        {buscando && <span className="rf-os-carregando" title="Procurando nos sistemas…" />}
      </div>

      {aberta && (
        <div className="rf-os-lista">
          {erroBusca && <p className="rf-os-vazio">{erroBusca}</p>}
          {!erroBusca && itens.length === 0 && !buscando && (
            <p className="rf-os-vazio">
              Nenhum sistema conhece essa OS ainda. Pode digitar do jeito que está — nada trava.
            </p>
          )}
          {itens.map((os, i) => (
            <button
              type="button"
              key={os.numero}
              className={`rf-os-item${i === marcado ? " marcado" : ""}`}
              onMouseEnter={() => setMarcado(i)}
              onClick={() => escolher(os)}
            >
              <span className="rf-os-num">
                {os.numero}
                {os.ja_tem_remessa && <em title="Já existe remessa desta OS">já cadastrada</em>}
              </span>
              <span className="rf-os-cliente">
                {[os.cliente, os.equipamento].filter(Boolean).join(" · ") || "sem cliente informado"}
              </span>
              <span className="rf-os-fontes">
                {os.fontes.map((fonte) => (
                  <em key={fonte}>{fonte}</em>
                ))}
                {os.local && <small>{os.local}</small>}
              </span>
            </button>
          ))}
          {avisos.map((a) => (
            <p className="rf-os-aviso-fonte" key={a}>
              <IconAlert size={12} /> {a}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/** Escolha curta desenhada em botões, no lugar do `<select>` do navegador. */
function ehChips(f: Field): boolean {
  if (f.type !== "select") return false;
  if (f.chips !== undefined) return f.chips;
  // De propósito conta só as opções da CONFIGURAÇÃO: se um cadastro antigo
  // acrescentar a 5ª opção (ver opcoesComAtual), o campo não pode trocar de
  // desenho no meio da lista só porque este registro tem um valor legado.
  return (f.options?.length ?? 0) > 0 && (f.options?.length ?? 0) <= 4;
}

/**
 * AS OPÇÕES DO CAMPO **MAIS** O VALOR QUE JÁ ESTÁ GRAVADO, quando ele está fora
 * da lista.
 *
 * POR QUE ISTO EXISTE: `<select>` e chips só sabem mostrar o que está em
 * `options`. Um equipamento cadastrado antes da lista fechada — classe de
 * tensão digitada como "13,8 kV" — aparecia em BRANCO na tela: o valor
 * continuava no banco (o formulário só grava o que a pessoa mexe), mas quem
 * abrisse a ficha para corrigir o fabricante via um campo vazio, não tinha como
 * saber o que estava lá e, se clicasse em qualquer opção, perdia o registro do
 * que o cadastro dizia. Trazer o valor atual como opção EXTRA resolve os dois
 * lados: ele continua visível e continua selecionado, e o rótulo "fora do
 * padrão" convida a trocar pelo texto da lista — que é o que faz o equipamento
 * casar com a tabela de limites da norma (a comparação é por texto exato).
 *
 * Vale para qualquer select do sistema, não só para a classe de tensão: é o
 * mesmo risco em toda lista que fechou depois que a base já tinha dados.
 */
// Exportada para poder ser conferida sozinha (é regra, não desenho).
export function opcoesComAtual(f: Field, valor: string): { options: string[]; labels: string[] } {
  const options = f.options ?? [];
  const labels = options.map((o, i) => f.optionLabels?.[i] ?? o);
  const atual = valor.trim();
  if (atual === "" || options.includes(atual)) return { options, labels };
  // A opção extra guarda o valor CRU (não o aparado): é ele que está no banco e
  // é por igualdade exata que o chip/`<select>` sabe qual opção está marcada —
  // aparar aqui deixaria o campo aceso em nada e salvaria um valor diferente do
  // que o cadastro tem hoje.
  return { options: [...options, valor], labels: [...labels, `${atual} — fora do padrão`] };
}

function renderInput(
  f: Field,
  value: unknown,
  set: (name: string, value: unknown) => void,
  erro: boolean,
) {
  const v = (value ?? "") as string | number;
  const invalido = erro ? { "aria-invalid": true as const } : {};

  switch (f.type) {
    case "select": {
      // O valor já gravado entra na lista quando está fora dela — senão o campo
      // apareceria vazio e o cadastro antigo sumiria da tela (ver opcoesComAtual).
      const { options: opcoes, labels: rotulos } = opcoesComAtual(f, String(v));
      if (ehChips(f)) {
        return (
          <Chips
            options={opcoes}
            labels={rotulos}
            value={String(v)}
            onChange={(val) => set(f.name, val)}
          />
        );
      }
      return (
        <div className="rf-select">
          <select value={v} {...invalido} onChange={(e) => set(f.name, e.target.value)}>
            <option value="">Selecione...</option>
            {opcoes.map((opt, i) => (
              <option key={opt} value={opt}>
                {rotulos[i] ?? opt}
              </option>
            ))}
          </select>
        </div>
      );
    }
    case "status":
      return (
        <StatusCardSelector
          options={f.options ?? []}
          hints={f.hints}
          value={v as string}
          onChange={(val) => set(f.name, val)}
        />
      );
    case "checklist": {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div>
          <ChecklistCardSelector
            options={f.checklistOptions ?? []}
            value={selected}
            onChange={(next) => set(f.name, next)}
          />
          {f.placeholder && <p className="modal-tab-hint" style={{ margin: "8px 0 0" }}>{f.placeholder}</p>}
        </div>
      );
    }
    case "textarea":
      return <AutoTextarea value={String(v)} placeholder={f.placeholder} onChange={(t) => set(f.name, t)} />;
    case "number":
      return <Numero f={f} valor={v} set={set} erro={erro} />;
    case "currency":
      return (
        <div className="rf-entrada">
          <span className="rf-prefixo">R$</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={v}
            {...invalido}
            placeholder={f.placeholder}
            onChange={(e) => set(f.name, e.target.value)}
          />
        </div>
      );
    case "date":
      return (
        <div className="rf-entrada">
          <input type="date" value={v} {...invalido} onChange={(e) => set(f.name, e.target.value)} />
          <button
            type="button"
            className="rf-mini"
            title="Usar a data de hoje"
            onClick={() => set(f.name, hoje())}
          >
            Hoje
          </button>
        </div>
      );
    case "email":
      return (
        <input
          type="email"
          value={v}
          {...invalido}
          placeholder={f.placeholder ?? "nome@empresa.com"}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "password":
      return (
        <input
          type="password"
          value={v}
          {...invalido}
          placeholder={f.placeholder}
          autoComplete="new-password"
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    default: {
      const lista = f.suggestions?.length ? `sug-${f.name}` : undefined;
      return (
        <div className={f.suffix ? "rf-entrada" : undefined}>
          <input
            type="text"
            value={v}
            {...invalido}
            list={lista}
            placeholder={f.placeholder}
            onChange={(e) => set(f.name, e.target.value)}
          />
          {f.suffix && <span className="rf-sufixo">{f.suffix}</span>}
          {lista && (
            <datalist id={lista}>
              {f.suggestions?.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
        </div>
      );
    }
  }
}

/** Escolha em botões: o valor gravado é o de `options`; a pessoa lê `labels`. */
function Chips({
  options,
  labels,
  value,
  onChange,
}: {
  options: string[];
  labels?: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="rf-chips">
      {options.map((opt, i) => (
        <button
          key={opt}
          type="button"
          className={`rf-chip${value === opt ? " active" : ""}`}
          aria-pressed={value === opt}
          // Clicar de novo no que já está marcado limpa: sem isso, escolha
          // errada num campo opcional não teria como ser desfeita.
          onClick={() => onChange(value === opt ? "" : opt)}
        >
          {value === opt && <IconCheck size={13} />}
          {labels?.[i] ?? opt}
        </button>
      ))}
    </div>
  );
}

/** Número com − e + (o passo vem da config; o padrão é 1). */
function Numero({
  f,
  valor,
  set,
  erro,
}: {
  f: Field;
  valor: string | number;
  set: (name: string, value: unknown) => void;
  erro: boolean;
}) {
  const passo = f.step ?? 1;
  const inteiro = Number.isInteger(passo);

  function mexer(dir: 1 | -1) {
    const atual = Number(valor);
    const base = Number.isFinite(atual) ? atual : 0;
    let novo = base + dir * passo;
    if (f.min != null && novo < f.min) novo = f.min;
    if (f.max != null && novo > f.max) novo = f.max;
    // Casas decimais do passo (0.1 não pode virar 0.30000000000000004).
    set(f.name, inteiro ? String(novo) : String(Number(novo.toFixed(4))));
  }

  return (
    <div className="rf-entrada rf-numero">
      <button type="button" className="rf-mini rf-mini-quad" onClick={() => mexer(-1)} title="Diminuir">
        −
      </button>
      <input
        type="number"
        step={f.step ?? "any"}
        min={f.min}
        max={f.max}
        value={valor}
        aria-invalid={erro || undefined}
        placeholder={f.placeholder}
        inputMode={inteiro ? "numeric" : "decimal"}
        onChange={(e) => set(f.name, e.target.value)}
      />
      {f.suffix && <span className="rf-sufixo">{f.suffix}</span>}
      <button type="button" className="rf-mini rf-mini-quad" onClick={() => mexer(1)} title="Aumentar">
        <IconPlus size={13} />
      </button>
    </div>
  );
}

/** Texto longo que cresce com o conteúdo, com contagem discreta. */
function AutoTextarea({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight + 2, 260)}px`;
  }, [value]);

  return (
    <div className="rf-textarea">
      <textarea
        ref={ref}
        rows={3}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {value.length > 0 && <span className="rf-contador">{value.length} caracteres</span>}
    </div>
  );
}
